<script lang="ts">
    import { select } from '@rxsv/core';
    import * as t from 'io-ts';

    import type { Setting } from 'portlet/models';
    import { Selectors } from 'portlet/store/filters-settings';
    import { Selectors as SettingsSelectors } from 'portlet/store/index';
    import { getStoreContext } from 'portlet/common/contexts';
    import { AppAction } from 'portlet/store';
    import { fromEnum } from '@bayer-int/portletce-shared';
    import { isLeft } from 'fp-ts/Either';
    import { ShortcutElementC } from 'portlet/models/settings/settingDefinitions/shortcutsSetting';
    import { isEqual } from 'lodash-es';
    import fontColorContrast from 'font-color-contrast';

    enum ShortcutColumns {
        LEFT = 'left',
        CENTER = 'center',
        RIGHT = 'right',
    }

    const ShortcutElementMoveC = t.intersection([
        ShortcutElementC,
        t.type({
            originalColumn: fromEnum<ShortcutColumns>(ShortcutColumns),
            originalIndex: t.number,
        }),
    ]);

    type ShortcutElement = t.TypeOf<typeof ShortcutElementC>;
    type ShortcutElementMove = t.TypeOf<typeof ShortcutElementMoveC>;

    const store = getStoreContext();

    const displayedShortcuts$ = store.state$.pipe(select(Selectors.displayedShortcuts));
    const activeSettings$ = store.state$.pipe(select(Selectors.settingsWithDefaults));
    const shortcutsColor$ = store.state$.pipe(
        select(SettingsSelectors.portletShortcutsColorSelector),
    );

    let container: HTMLDivElement | undefined = $state();

    let shortcuts = $derived($displayedShortcuts$);
    let leftColumn = $derived(shortcuts.left);
    let centerColumn = $derived(shortcuts.center);
    let rightColumn = $derived(shortcuts.right);
    let hasNoShortcuts = $derived(Object.values(shortcuts).every(arr => arr.length === 0));
    let emptyColumns = $derived({
        left: !leftColumn.length,
        center: !centerColumn.length,
        right: !rightColumn.length,
    });
    let activeSettings = $derived($activeSettings$);
    let backgroundColor = $derived($shortcutsColor$);

    function setSetting(setting: Setting) {
        store.action$.next(AppAction.filters.CHANGE_SETTING_VALUE(setting));
    }

    function isShortcutActive(shortcut: ShortcutElement): boolean {
        return isEqual(activeSettings[shortcut.key], shortcut.value);
    }

    function calculateFontColor(bgColor: string | undefined): string {
        const rgbMatch = bgColor && bgColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
        if (rgbMatch) {
            const r = parseInt(rgbMatch[1], 10);
            const g = parseInt(rgbMatch[2], 10);
            const b = parseInt(rgbMatch[3], 10);
            return fontColorContrast([r, g, b]);
        }

        return bgColor ? fontColorContrast(bgColor) : 'white';
    }

    function handleDragEnter(e: DragEvent) {
        (e.target as HTMLElement)?.classList.add('on-drag');
    }

    function handleDragLeave(e: DragEvent) {
        (e.target as HTMLElement)?.classList.remove('on-drag');
        document
            .querySelector('portlet-ce')
            ?.shadowRoot?.querySelector('header')
            ?.classList.remove('on-drag');
    }

    function allowDrop(e: DragEvent) {
        e.preventDefault();
    }

    function handleDragDropInContainer(e: DragEvent, column: ShortcutColumns) {
        const target = e.target as HTMLElement;
        target.classList.remove('on-drag');
        column && handleDragDrop(e, column);
        container?.parentElement?.querySelector('header')?.classList.remove('on-drag');
    }

    function handleDragDrop(e: DragEvent, column: ShortcutColumns) {
        e.preventDefault();
        e.stopPropagation();
        hideDeleteArea();

        const settingJSON = e.dataTransfer?.getData('setting');
        const settingShortcutMoveJSON = e.dataTransfer?.getData('settingShortcutMove');

        const newSetting = settingJSON
            ? formatShortcutAdd(settingJSON, column)
            : settingShortcutMoveJSON
              ? formatShortcutMove(settingShortcutMoveJSON, column)
              : null;

        if (!newSetting) return;
        store.action$.next(AppAction.filters.CHANGE_SETTING_VALUE(newSetting));
    }

    function formatShortcutAdd(settingJSON: string, column: ShortcutColumns): Setting | null {
        const settingDecoded = ShortcutElementC.decode(JSON.parse(settingJSON));
        if (isLeft(settingDecoded)) return null;

        const { right: setting } = settingDecoded;

        return {
            key: 'SHORTCUTS',
            value:
                column === ShortcutColumns.LEFT
                    ? { ...shortcuts, left: [...shortcuts.left, setting] }
                    : column === ShortcutColumns.CENTER
                      ? { ...shortcuts, center: [...shortcuts.center, setting] }
                      : { ...shortcuts, right: [...shortcuts.right, setting] },
        };
    }

    function formatShortcutMove(settingJSON: string, column: ShortcutColumns): Setting | null {
        const settingShortcut = ShortcutElementMoveC.decode(JSON.parse(settingJSON));
        if (isLeft(settingShortcut)) return null;

        const { key, label, value, originalColumn, originalIndex } = settingShortcut.right;
        const setting: ShortcutElement = { key, value, label };

        if (originalColumn === column) return null;

        return {
            key: 'SHORTCUTS',
            value:
                column === ShortcutColumns.LEFT
                    ? {
                          ...shortcuts,
                          left: [...shortcuts.left, setting],
                          [originalColumn]: [
                              ...shortcuts[originalColumn].filter((_, i) => i !== originalIndex),
                          ],
                      }
                    : column === ShortcutColumns.CENTER
                      ? {
                            ...shortcuts,
                            center: [...shortcuts.center, setting],
                            [originalColumn]: [
                                ...shortcuts[originalColumn].filter((_, i) => i !== originalIndex),
                            ],
                        }
                      : {
                            ...shortcuts,
                            right: [...shortcuts.right, setting],
                            [originalColumn]: [
                                ...shortcuts[originalColumn].filter((_, i) => i !== originalIndex),
                            ],
                        },
        };
    }

    function handleDragStart(e: DragEvent, idx: number, column: ShortcutColumns) {
        if (!e.dataTransfer) return;
        e.dataTransfer.dropEffect = 'move';
        (e.target as HTMLElement).parentElement
            ?.querySelector('.delete-drop-area')
            ?.classList.remove('delete-drop-area--hidden');

        const shortcut =
            column === ShortcutColumns.LEFT
                ? {
                      ...shortcuts.left[idx],
                      originalColumn: ShortcutColumns.LEFT,
                      originalIndex: idx,
                  }
                : column === ShortcutColumns.CENTER
                  ? {
                        ...shortcuts.center[idx],
                        originalColumn: ShortcutColumns.CENTER,
                        originalIndex: idx,
                    }
                  : {
                        ...shortcuts.right[idx],
                        originalColumn: ShortcutColumns.RIGHT,
                        originalIndex: idx,
                    };

        e.dataTransfer.setData('settingShortcutMove', JSON.stringify(shortcut));
    }

    function deleteShortcut(e: DragEvent) {
        e.preventDefault();
        hideDeleteArea();

        const settingShortcutMoveJSON = e.dataTransfer?.getData('settingShortcutMove');

        if (!settingShortcutMoveJSON) return;
        const settingShortcut = ShortcutElementMoveC.decode(JSON.parse(settingShortcutMoveJSON));
        if (isLeft(settingShortcut)) return;

        const { originalColumn, originalIndex } = settingShortcut.right;

        store.action$.next(
            AppAction.filters.CHANGE_SETTING_VALUE({
                key: 'SHORTCUTS',
                value: {
                    ...shortcuts,
                    [originalColumn]: [
                        ...shortcuts[originalColumn].filter((_, i) => i !== originalIndex),
                    ],
                },
            }),
        );
    }

    function hideDeleteArea() {
        container
            ?.querySelectorAll('.delete-drop-area')
            .forEach(el => el.classList.add('delete-drop-area--hidden'));
    }
</script>

<div class="settings-shortcuts" class:empty={hasNoShortcuts} bind:this={container}>
    <div
        class="settings-shortcuts-column column-left"
        class:empty={emptyColumns.left}
        class:on-drag={false}
        aria-colindex={1}
        ondragenter={handleDragEnter}
        ondragleave={handleDragLeave}
        ondragover={allowDrop}
        ondrop={e => handleDragDropInContainer(e, ShortcutColumns.LEFT)}
    >
        {#each leftColumn as shortcut, idx}
            <button
                class="settings-shortcuts__button"
                class:active={isShortcutActive(shortcut)}
                style="background-color: {backgroundColor}; color: {calculateFontColor(
                    backgroundColor,
                )}"
                onclick={() => setSetting(shortcut)}
                draggable="true"
                ondragstart={e => handleDragStart(e, idx, ShortcutColumns.LEFT)}
                ondragend={hideDeleteArea}
                ondrop={e => handleDragDrop(e, ShortcutColumns.LEFT)}>{shortcut.label}</button
            >
        {/each}
        <button
            class="delete-drop-area delete-drop-area--hidden"
            class:on-drag={false}
            ondragenter={handleDragEnter}
            ondragleave={handleDragLeave}
            ondrop={deleteShortcut}
        >
            <i class="material-icons">delete</i>
        </button>
    </div>
    <div
        class="settings-shortcuts-column column-center"
        class:empty={emptyColumns.center}
        aria-colindex={2}
        ondragenter={handleDragEnter}
        ondragleave={handleDragLeave}
        ondragover={allowDrop}
        ondrop={e => handleDragDropInContainer(e, ShortcutColumns.CENTER)}
    >
        {#each centerColumn as shortcut, idx}
            <button
                class="settings-shortcuts__button"
                class:active={isShortcutActive(shortcut)}
                style="background-color: {backgroundColor}; color: {calculateFontColor(
                    backgroundColor,
                )}"
                onclick={() => setSetting(shortcut)}
                draggable="true"
                ondragstart={e => handleDragStart(e, idx, ShortcutColumns.CENTER)}
                ondragend={hideDeleteArea}
                ondrop={e => handleDragDrop(e, ShortcutColumns.CENTER)}>{shortcut.label}</button
            >
        {/each}
        <button
            class="delete-drop-area delete-drop-area--hidden"
            ondragenter={handleDragEnter}
            ondragleave={handleDragLeave}
            ondrop={deleteShortcut}
        >
            <i class="material-icons">delete</i>
        </button>
    </div>
    <div
        class="settings-shortcuts-column column-right"
        class:empty={emptyColumns.right}
        aria-colindex={3}
        ondragenter={handleDragEnter}
        ondragleave={handleDragLeave}
        ondragover={allowDrop}
        ondrop={e => handleDragDropInContainer(e, ShortcutColumns.RIGHT)}
    >
        {#each rightColumn as shortcut, idx}
            <button
                class="settings-shortcuts__button"
                class:active={isShortcutActive(shortcut)}
                style="background-color: {backgroundColor}; color: {calculateFontColor(
                    backgroundColor,
                )}"
                onclick={() => setSetting(shortcut)}
                draggable="true"
                ondragstart={e => handleDragStart(e, idx, ShortcutColumns.RIGHT)}
                ondragend={hideDeleteArea}
                ondrop={e => handleDragDrop(e, ShortcutColumns.RIGHT)}>{shortcut.label}</button
            >
        {/each}
        <button
            class="delete-drop-area delete-drop-area--hidden"
            ondragenter={handleDragEnter}
            ondragleave={handleDragLeave}
            ondrop={deleteShortcut}
        >
            <i class="material-icons">delete</i>
        </button>
    </div>
</div>

<style lang="scss" scoped>
    .settings-shortcuts {
        gap: 0.3rem;
        display: flex;
        justify-content: space-between;
        padding: 0.3rem;
        padding-top: 0;

        &.empty {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            position: absolute;
            top: 1.5rem;
            width: 100%;
        }

        &-column {
            display: flex;
            align-content: flex-start;
            flex-wrap: wrap;
            min-height: 10px;
            min-width: 20px;
            gap: 0.3rem;
            position: relative;
            padding-bottom: 0.33rem;

            &.empty {
                flex: 0.3 0.3;
            }

            &.on-drag {
                border-radius: 10px;
                animation: background-color-pulse 0.7s ease-in-out infinite alternate;
                z-index: 2;
                min-height: 40px;
            }
        }

        .column-left {
            justify-content: start;
            padding-left: 0.7rem; //used 0.7rem, because main container has 0.3rem. In total it gives 1rem like in Header.svelte
        }
        .column-center {
            justify-content: center;
        }
        .column-right {
            justify-content: end;
            padding-right: 0.7rem; //used 0.7rem, because main container has 0.3rem. In total it gives 1rem like in Header.svelte
        }

        &__button {
            background: #3d77bc;
            color: #fff;
            border: 1px solid rgba(0, 0, 0, 0.2);
            outline: none;
            padding: 3px 5px;
            border-radius: 4px;
            cursor: pointer;
            height: fit-content;
            opacity: 0.6;

            &:hover {
                filter: brightness(1.1);
            }
            &.active {
                opacity: 1;
            }
        }

        .delete-drop-area {
            position: absolute;
            bottom: -70px;
            z-index: 2;
            color: #f00;
            background-color: rgba(255, 255, 255, 0.8);
            border-radius: 99rem;
            height: 3rem;
            width: 3rem;
            box-shadow: 0px 0px 10px red;
            border: 1px solid;
            transition: all 0.3s;
            margin: 0 3rem;

            &--hidden {
                height: 0;
                width: 0;
                opacity: 0;
                z-index: 0;

                .material-icons {
                    opacity: 0;
                }
            }

            &.on-drag {
                background-color: #fff;
                box-shadow: 0px 0px 20px red;
            }
        }
    }

    @keyframes background-color-pulse {
        from {
            background-color: #00000000;
        }
        to {
            background-color: #00000020;
            // background-color: #e7e7e7;
        }
    }
</style>
